import { FC, ReactNode } from 'react'

// Imported from the leaf task-schema module rather than the `conductor`
// barrel: the barrel pulls in `getTaskReferenceName`, which imports
// `nodeDefinitions/types` and cycles back to this file. Type-only, so it
// erases at build time either way.
import type { AutomationsPresentationV1Node } from 'Automations/automationEditor/types'
import type { ConductorTask } from '../conductor/schema/task'
import type { SemanticType } from '../semanticTypes'
import { ValidationResult, ValidatorReturn } from '../validation'

export type ConditionalProperties = Record<string, ConditionalProperty>

/**
 * The shape of a single condition row stored on a switch (or any future
 * branching node that uses `ConditionalOperator`s). Lives here so operator
 * config callbacks (e.g. `gridStyle`) can reference it without depending on
 * the switch module.
 */
export type Condition = {
  id: string
  property: string
  operator: string
  value: any
}

/**
 * Constant used by amount-with-unit values to indicate the percentage unit
 * was selected. The amount function strings switch on this constant; any
 * other unit value is treated as a currency code.
 */
export const PERCENTAGE_UNIT = 'PERCENTAGE'

export type ConditionGridStyle = {
  gridTemplateAreas?: string
  gridTemplateColumns?: string
  gap?: string
}

export type ConditionalFunctionString = (params: {
  /**
   * Runtime variable path of the condition's property — e.g.
   * `'$["sequence.quote.totalContractValue"]'`. Operators that need to
   * reference *other* upstream values (currency, percentage base, etc.)
   * declare those dependencies up front in their factory config as
   * semantic-type strings, not via this parameter.
   */
  property: string
  /**
   * The condition's stored value. Operators are free to use whatever shape
   * suits their input component (a primitive, an array, or a richer object
   * like `{ amount, unit }` for amount-with-unit comparisons). The operator's
   * `functionString` is responsible for unpacking this shape into the runtime
   * expression.
   */
  value: any
}) => string

/**
 * Editor context surfaced to a value input.
 */
export type ConditionValueInputContext = {
  /**
   * True for one render after the user picks this row's property when the
   * property opted out of auto-opening its operator dropdown (i.e.
   * `focusOperatorOnSelect: false`). Lets the value input take the focus
   * itself — for instance, by auto-opening its own primary picker.
   */
  focusOnInitialLoad: boolean
}

/**
 * Props passed to an operator's `valueInputComponent`. The component owns the UI for
 * editing the condition's `value` and emits updates via `onChange`. Components
 * are free to use any shape for `value` — a primitive, an array, or a richer
 * object — provided their operator's `functionString` and `formatValue` know
 * how to read it back.
 */
export type ConditionValueInputComponentProps = {
  'data-testid'?: string
  /** The property the condition is being defined against. */
  property: string
  value: any
  onChange: (next: { value: any }) => void
  validationErrors?: ValidationResult[]
  context: ConditionValueInputContext
}

export type ConditionValueInputComponent = FC<ConditionValueInputComponentProps>

/**
 * Formats the condition's value for display in labels / canvas previews.
 * Operators that store a richer value shape on the condition unpack it here.
 * Returning a ReactNode lets formatters render bespoke UI (tooltips, badges,
 * etc.) on the canvas; consumers that need a plain string (e.g. the execution
 * summary group label) coerce via String() and so should return primitives
 * there.
 */
export type ConditionalValueFormatter = (params: { value: any }) => ReactNode
/**
 * Formats the operator segment of a label. Receives the row's value as well as
 * its operator, so a property whose subject varies with the value (e.g. which
 * part of a template's pricing is compared) can name it here.
 */
export type OperatorFormatter = (params: {
  operator: string
  value: any
}) => ReactNode

/**
 * Property-level value formatter. Renders a label segment between the
 * property label and the operator section — useful when the property as a
 * whole has a primary "subject" (e.g. the chosen product on the PRODUCT
 * property) that should be surfaced before the operator does its own
 * formatting. Receives both the condition's value and the chosen operator
 * so the property can vary its rendering across operators.
 */
export type ConditionalPropertyValueFormatter = (params: {
  value: any
  operator: string
  /**
   * The nodes the clause is read against. A property naming another node
   * resolves it here, so the clause reads the same on the canvas and in an
   * execution's history — which has a saved version's nodes, not the editor's.
   */
  nodes: AutomationsPresentationV1Node[]
}) => ReactNode
export type ConditionalOperator = {
  label: string
  /**
   * Semantic type(s) this operator depends on. Overrides the parent
   * property's `valueSemanticType` when set — the operator is hidden
   * from the operator dropdown unless every listed type is present in
   * the upstream outputs, and the first entry becomes the `property`
   * runtime path passed to the function string. Use this when operators
   * on the same property read different upstream values (e.g. PRODUCT's
   * DISCOUNT operators read `productDiscounts` while PRICING_MODEL
   * reads `phases`).
   */
  valueSemanticType?: SemanticType | SemanticType[]
  /**
   * The component rendered as the operator's value editor. Owns the UI for
   * the value (including any unit selection it manages internally) and emits
   * `{ value }` via `onChange`. Ignored when the parent property defines a
   * `propertyEditorComponent` — in that case the property component owns the
   * entire row UI.
   */
  valueInputComponent?: ConditionValueInputComponent
  requiresValue?: boolean
  /**
   * Operator keys that cannot coexist with this operator on the same property.
   * Used by `getAvailableOperators` to filter the operator dropdown when
   * another condition on the same property already uses a conflicting operator.
   *
   * Example: IS_EMPTY declares `exclusiveOf: ['IS_NOT_EMPTY', 'INCLUDES', 'EXCLUDES']`
   * so selecting "Is empty" hides all other operators for that property.
   */
  exclusiveOf?: string[]
  /**
   * Optional formatter for label rendering (canvas, switch group label,
   * config preview). Falls back to `String(value)` when omitted.
   */
  formatPreviewValue?: ConditionalValueFormatter
  formatPreviewOperator?: OperatorFormatter
  /**
   * Optional per-operator value validation. Composes with the property-level
   * `validateProperty` when both are defined — both run and their results
   * are concatenated. Returning `undefined` means valid; otherwise return
   * one or more `ValidatorReturn` records. Each result may attach a
   * `metadata` payload (e.g. `{ field: 'productId' }`) so editors can route
   * the error to a specific sub-input.
   */
  validateValue?: (params: {
    value: unknown
  }) => ValidatorReturn | ValidatorReturn[] | undefined
  /**
   * Optional grid style applied to the row that holds the operator dropdown
   * and the value input. Ignored when the parent property defines a
   * `propertyEditorComponent`.
   */
  gridStyle?: (condition: Condition) => ConditionGridStyle
  functionString: ConditionalFunctionString
}

/**
 * Props passed to a property-level editor. When a property defines a
 * `propertyEditorComponent`, the framework hands over the entire post-property
 * row UI — operator dropdown, value editing, layout — to this component.
 * Operators on the property remain responsible for their behavioural concerns
 * (`functionString`, `formatPreview*`, `validateValue`).
 */
export type ConditionPropertyEditorComponentProps = {
  'data-testid'?: string
  /**
   * The condition's property picker when the property opts into an inline
   * editor layout. This lets a property editor place its primary subject next
   * to the property picker instead of always starting on the following row.
   */
  propertyInput?: ReactNode
  /**
   * The condition's remove button when the property opts into an inline
   * editor layout. The editor can keep it on the first row while allowing
   * later value rows to use the full condition width beneath it.
   */
  removeButton?: ReactNode
  /**
   * Every node in the workflow. A property whose subject is one of them builds
   * its options from all of them; where a node sits is validation's to judge.
   */
  nodes: AutomationsPresentationV1Node[]
  operator: string
  value: any
  onChange: (next: { operator?: string; value?: any }) => void
  /**
   * Operator definitions for this property. Components render their own
   * operator dropdown from these and may reach for `formatPreview*` etc.
   */
  operators: Record<string, ConditionalOperator>
  /**
   * Full `Condition` records for the other rows in the same group. The
   * property component decides cross-operator rules from here.
   */
  siblingConditions: Array<Condition>
  /**
   * Validation errors scoped to the two fields the property editor owns —
   * the operator dropdown and the stored value. Editors route the value
   * errors to the right sub-input via `metadata.field`.
   */
  validationErrors?: {
    operator?: ValidationResult[]
    value?: ValidationResult[]
  }
  focusOnInitialLoad: boolean
}

export type ConditionPropertyEditorComponent =
  FC<ConditionPropertyEditorComponentProps>

/**
 * Tasks a condition needs run before its node can be evaluated, plus the
 * runtime keys their outputs are published under.
 */
export type ConditionSupportingTask = {
  /**
   * An ordered branch of tasks to insert before the condition node. A task may
   * read the output of any task before it; the node runs the branch serially and
   * evaluates once its last task finishes.
   *
   *   [getQuoteTemplate_tpl-a, compareQuotePricing_tpl-a]
   *
   * Names must derive from the task's *subject* (e.g. a template id), never the
   * condition row, so two rows needing the same data converge on one task — the
   * node merges branches on `taskReferenceName`. Reusing a name for a different
   * definition throws at generation time.
   */
  tasks: ConductorTask[]
  /**
   * Runtime key -> task output path, merged into the condition node's input
   * parameters. Keys are scoped semantic types (see `scopedSemanticTypeKey`)
   * so two rows targeting different subjects can't shadow each other.
   */
  semanticTypeMapping: Record<string, string>
}

/**
 * Builds the tasks a condition row depends on. Called once per configured row
 * at generation time.
 *
 * Must return `undefined` — never throw — for an incomplete value: generation
 * runs on every save, and a throw there is swallowed and replaced with an
 * inert placeholder workflow.
 */
export type ConditionValueSupportingTask = (params: {
  /** Id of the node holding the condition (today, the switch). */
  conditionNodeId: string
  conditionId: string
  operator: string
  siblingConditions: Array<Condition>
  value: any
  /**
   * Every node in the workflow. A property whose comparison depends on another
   * node's config reads it here; the node's own id is a stored value on the
   * row, so this is the lookup table for it.
   */
  nodes: AutomationsPresentationV1Node[]
  /**
   * The node's own input mapping — semantic type -> raw task output path,
   * unwrapped. A returned task builds its `inputParameters` from this as a
   * node's `taskTransformer` does, wrapping at the point of use:
   *
   *   data: wrapVariables({ source: inputMapping[semanticTypes.sequence.quote.phases] })
   *
   * A semantic type absent here isn't published upstream, so return `undefined`
   * rather than a task referencing a path that will never resolve.
   */
  inputMapping: Record<string, string>
}) => ConditionSupportingTask | undefined

export type ConditionalProperty = {
  label: string
  /**
   * Default semantic type(s) for the property's operators. Optional —
   * operators that don't declare their own `valueSemanticType` fall
   * back to this one. Use it for properties whose operators all read
   * the same upstream value (e.g. TOTAL_CONTRACT_VALUE). When operators
   * read different upstream values (e.g. PRODUCT's DISCOUNT vs
   * PRICING_MODEL), set `valueSemanticType` on each operator instead
   * and leave this undefined.
   *
   * When defined, the property is hidden in the picker unless every
   * listed type is present in the upstream outputs. The first entry
   * becomes the `property` runtime path passed to function strings.
   */
  valueSemanticType?: SemanticType | SemanticType[]
  operators: Record<string, ConditionalOperator>
  /**
   * Optional formatter rendered between the property label and the
   * operator's own formatting. Use this to surface a property-wide subject
   * (e.g. the chosen product on the PRODUCT property) so operators can keep
   * their `formatValue` focused on just the comparison.
   */
  formatPreviewProperty?: ConditionalPropertyValueFormatter
  /**
   * When `false`, the operator dropdown does not auto-open after the user
   * picks this property. Use it for properties whose operator dropdown is
   * trivial (e.g. a single option) so the user isn't shown a redundant
   * popover. Defaults to `true`.
   */
  focusOperatorOnSelect?: boolean
  /**
   * When defined, replaces the framework's default "operator dropdown +
   * operator-owned value input" with a single component that owns the
   * entire row UI. Operators on this property still provide functional
   * config (`functionString`, `formatPreview*`, `validateValue`) but their
   * `valueInputComponent` / `gridStyle` are ignored.
   */
  propertyEditorComponent?: ConditionPropertyEditorComponent
  /**
   * Places the property picker inside the property editor so it can share the
   * first row with the editor's primary control. Defaults to the standard
   * stacked layout, where the property picker occupies its own row.
   */
  propertyEditorLayout?: 'inline'
  /**
   * Optional property-level validation hook. Composes with each operator's
   * own `validateValue` when both are defined. Receives the chosen operator
   * and value so the property can dispatch (e.g. PRODUCT delegates to
   * per-operator field validators living next to the property definition).
   */
  validateProperty?: (params: {
    operator: string
    value: unknown
    /** The other conditions in the same group as this condition. */
    siblingConditions: Array<Condition>
    /** Every node in the workflow — the same list the editor offers from. */
    nodes: AutomationsPresentationV1Node[]
    /**
     * Of those, the ones the condition node leads to, so a property whose
     * subject is a node can check the user picked one that actually follows it.
     */
    downstreamNodes: AutomationsPresentationV1Node[]
  }) => ValidatorReturn | ValidatorReturn[] | undefined
  /**
   * Optional override for whether this property appears in the property
   * dropdown for a given row. Receives the other rows' conditions in the
   * same group so the property can decide (e.g. PRODUCT stays available
   * across multiple rows because each row targets a different product —
   * the cross-row reasoning happens inside its own editor). When omitted,
   * the framework defaults to "available iff at least one operator is
   * still available for this property" — which excludes properties whose
   * operators are all consumed by siblings.
   */
  propertyAvailable?: (params: {
    siblingConditions: Array<Condition>
    /**
     * Every node in the workflow. A property whose subject is one of them hides
     * itself when the workflow holds none to pick.
     */
    nodes: AutomationsPresentationV1Node[]
  }) => boolean
  /**
   * Picks the operator when this property is selected. Use this when a custom
   * property editor permits repeated primary operators and decides viability
   * from more detailed values held by sibling rows.
   */
  getDefaultOperator?: (params: {
    siblingConditions: Array<Condition>
  }) => string | undefined
  /**
   * Optional hook for properties whose comparison needs data the trigger
   * payload doesn't carry (e.g. a user-chosen quote template). The condition
   * node runs the returned tasks before evaluating itself, and publishes the
   * returned mapping alongside its upstream inputs.
   */
  valueSupportingTask?: ConditionValueSupportingTask
}
